function safeTrim(value) {
  return value == null ? "" : String(value).trim();
}

function titleCaseWord(value = "") {
  const raw = safeTrim(value);
  if (!raw) return "";

  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function humanizeSlug(value = "") {
  const cleaned = safeTrim(value)
    .replace(/[-_]+/g, " ")
    .replace(/\b\d+\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "";

  return cleaned
    .split(" ")
    .map((part) => titleCaseWord(part))
    .join(" ")
    .trim();
}

function normalizeHttpUrl(value = "") {
  const raw = safeTrim(value);
  if (!raw) return "";

  const candidate =
    /^https?:\/\//i.test(raw) || raw.startsWith("mailto:") || raw.startsWith("tel:")
      ? raw
      : `https://${raw.replace(/^\/+/, "")}`;

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.toString();
  } catch {
    return "";
  }
}

function getParsedUrl(value = "") {
  const normalized = normalizeHttpUrl(value);
  if (!normalized) return null;

  try {
    return new URL(normalized);
  } catch {
    return null;
  }
}

function getPrimaryDomainLabel(hostname = "") {
  const parts = safeTrim(hostname)
    .replace(/^www\./i, "")
    .split(".")
    .filter(Boolean);

  if (!parts.length) return "";
  if (parts.length === 1) return parts[0];
  return parts[parts.length - 2] || "";
}

const X_PROFILE_RESERVED_SEGMENTS = new Set([
  "explore",
  "hashtag",
  "home",
  "i",
  "intent",
  "login",
  "messages",
  "notifications",
  "search",
  "settings",
  "share",
  "tos",
]);

function hostnameMatches(hostname = "", allowedHosts = []) {
  const normalizedHost = safeTrim(hostname).replace(/^www\./i, "").toLowerCase();
  return allowedHosts.some(
    (host) => normalizedHost === host || normalizedHost.endsWith(`.${host}`)
  );
}

function isXProfileHost(hostname = "") {
  return hostnameMatches(hostname, ["x.com", "twitter.com"]);
}

function inferSourceBucket(hostname = "") {
  const host = safeTrim(hostname).toLowerCase();
  if (!host) return "other";

  if (host.includes("linkedin.")) return "linkedin";
  if (isXProfileHost(host)) return "social_profile";
  if (host.includes("indeed.")) return "indeed";

  if (
    host.includes("greenhouse.") ||
    host.includes("lever.") ||
    host.includes("workable.") ||
    host.includes("ashbyhq.") ||
    host.includes("jobvite.") ||
    host.includes("ziprecruiter.") ||
    host.includes("smartrecruiters.") ||
    host.includes("monster.")
  ) {
    return "job_board";
  }

  return "website";
}

function mapSourceForEntity(bucket = "other", entity = "candidate") {
  const normalizedEntity = safeTrim(entity).toLowerCase();

  if (normalizedEntity === "job") {
    if (bucket === "linkedin") return "LinkedIn";
    if (bucket === "indeed") return "Indeed";
    if (bucket === "job_board") return "Job Board";
    if (bucket === "website") return "Company Website";
    return "";
  }

  if (normalizedEntity === "lead" || normalizedEntity === "contact") {
    if (bucket === "linkedin") return "LinkedIn";
    if (bucket === "indeed") return "Indeed";
    if (bucket === "social_profile") return "Other";
    if (bucket === "website") return "Website";
    return "Other";
  }

  if (normalizedEntity === "company") {
    if (bucket === "linkedin") return "LinkedIn";
    if (bucket === "job_board" || bucket === "indeed") return "Job board";
    if (bucket === "social_profile") return "Other";
    if (bucket === "website") return "Website";
    return "Other";
  }

  if (bucket === "linkedin") return "LinkedIn";
  if (bucket === "job_board" || bucket === "indeed") return "Job board";
  if (bucket === "social_profile") return "Other";
  if (bucket === "website") return "Website";
  return "Other";
}

function inferProfilePlatform(hostname = "") {
  const host = safeTrim(hostname).replace(/^www\./i, "").toLowerCase();
  if (!host) return "";
  if (host.includes("linkedin.")) return "LinkedIn";
  if (isXProfileHost(host)) return "X";
  return "";
}

function inferCompanyNameFromParsedUrl(parsedUrl) {
  if (!parsedUrl) return "";

  const hostname = safeTrim(parsedUrl.hostname).replace(/^www\./i, "").toLowerCase();
  const segments = parsedUrl.pathname.split("/").filter(Boolean);

  if (hostname.includes("linkedin.")) {
    if (segments[0] === "company" && segments[1]) return humanizeSlug(segments[1]);
    return "";
  }

  if (isXProfileHost(hostname)) {
    return "";
  }

  if (hostname.includes("jobs.lever.")) {
    return humanizeSlug(segments[0] || "");
  }

  if (hostname.includes("boards.greenhouse.")) {
    return humanizeSlug(segments[0] || "");
  }

  if (hostname.includes("jobs.ashbyhq.")) {
    const candidate = segments[0] || "";
    if (!candidate || /^(application|apply)$/i.test(candidate)) return "";
    return humanizeSlug(candidate);
  }

  if (hostname.includes("workable.")) {
    const subdomain = hostname.split(".")[0];
    if (subdomain && !["www", "apply", "jobs"].includes(subdomain)) {
      return humanizeSlug(subdomain);
    }
    return humanizeSlug(segments[0] || "");
  }

  if (hostname.includes("smartrecruiters.")) {
    return humanizeSlug(segments[0] || "");
  }

  if (hostname.includes("jobvite.")) {
    const subdomain = hostname.split(".")[0];
    if (subdomain && subdomain !== "jobs") return humanizeSlug(subdomain);
  }

  if (hostname.includes("ziprecruiter.") || hostname.includes("monster.")) {
    return humanizeSlug(segments[0] || "");
  }

  return humanizeSlug(getPrimaryDomainLabel(hostname));
}

function inferProfileNameFromParsedUrl(parsedUrl) {
  if (!parsedUrl) return "";

  const hostname = safeTrim(parsedUrl.hostname).replace(/^www\./i, "").toLowerCase();
  const segments = parsedUrl.pathname.split("/").filter(Boolean);

  if (hostname.includes("linkedin.")) {
    if (!["in", "pub"].includes((segments[0] || "").toLowerCase())) return "";
    return humanizeSlug(segments[1] || "");
  }

  if (!isXProfileHost(hostname)) return "";

  const candidate = safeTrim(segments[0] || "").replace(/^@+/, "");
  if (!candidate || X_PROFILE_RESERVED_SEGMENTS.has(candidate.toLowerCase())) {
    return "";
  }

  return humanizeSlug(candidate);
}

function inferWebsiteFromParsedUrl(parsedUrl, sourceBucket = "other") {
  if (!parsedUrl || sourceBucket !== "website") return "";
  return `${parsedUrl.protocol}//${parsedUrl.hostname}`;
}

export function deriveLinkAutofill(value = "", entity = "candidate") {
  const rawValue = safeTrim(value);
  const parsedUrl = getParsedUrl(rawValue);
  const normalizedUrl = parsedUrl?.toString() || "";
  const hostname = safeTrim(parsedUrl?.hostname).replace(/^www\./i, "").toLowerCase();
  const sourceBucket = inferSourceBucket(hostname);

  return {
    rawValue,
    normalizedUrl,
    hostname,
    sourceBucket,
    source: mapSourceForEntity(sourceBucket, entity),
    companyName: inferCompanyNameFromParsedUrl(parsedUrl),
    profileName: inferProfileNameFromParsedUrl(parsedUrl),
    profilePlatform: inferProfilePlatform(hostname),
    website: inferWebsiteFromParsedUrl(parsedUrl, sourceBucket),
  };
}

export function isLinkedInProfileUrl(value = "") {
  const url = getParsedUrl(value);
  return Boolean(
    url && hostnameMatches(url.hostname, ["linkedin.com"]) &&
    /^\/(?:in|pub)\/[^/]+/i.test(url.pathname)
  );
}

export function describeLinkAutofill(info = {}, options = {}) {
  const includeProfile = options.includeProfile === true;
  const includeWebsite = options.includeWebsite === true;
  const parts = [];

  if (safeTrim(info?.source)) parts.push(`Source: ${info.source}`);
  if (safeTrim(info?.companyName)) parts.push(`Company: ${info.companyName}`);
  if (includeProfile && safeTrim(info?.profileName)) {
    parts.push(
      safeTrim(info?.profilePlatform)
        ? `${info.profilePlatform}: ${info.profileName}`
        : `Profile: ${info.profileName}`
    );
  }
  if (includeWebsite && safeTrim(info?.website)) parts.push(`Website: ${info.website}`);

  return parts.join(" | ");
}
